import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { gql } from "@apollo/client";
import { client } from "..";



const GET_ALL_DEPARTMENT_ACCESS = gql`
  query GetAllDepartmentAccess($includeInactive: Boolean) {
    getAllDepartmentAccess(includeInactive: $includeInactive) {
      _id
      department
      routes
      updatedBy
      updatedAt
      status
    }
  }
`;


const GET_ALL_EMPLOYEE_ACCESS = gql`
  query GetAllEmployeeAccess($includeInactive: Boolean) {
    getAllEmployeeAccess(includeInactive: $includeInactive) {
      _id
      employeeId
      employeeName
      employeeDepartment
      routes
      employees
      updatedBy
      updatedAt
      status
    }
  }
`;

const GET_EMPLOYEE_ALL_ROUTES = gql`
  query GetEmployeeAllRoutes($employeeId: String!, $employeeDepartment: String!) {
    getEmployeeAllRoutes(employeeId: $employeeId, employeeDepartment: $employeeDepartment)
  }
`;

const GET_EMPLOYEES_BY_ROUTE = gql`
  query GetEmployeesByRoute($route: String!) {
    getEmployeesByRoute(route: $route) {
      id
      EmpID
      firstname
      lastname
      department
      designation
      employeestatus
      image
      role
    }
  }
`;

const CHECK_EMPLOYEE_ROUTE_ACCESS = gql`
  query CheckEmployeeRouteAccess(
    $employeeId: String!
    $employeeDepartment: String!
    $route: String!
  ) {
    checkEmployeeRouteAccess(
      employeeId: $employeeId
      employeeDepartment: $employeeDepartment
      route: $route
    )
  }
`;

const UPSERT_DEPARTMENT_ACCESS = gql`
  mutation UpsertDepartmentAccess(
    $department: String!
    $routesToAdd: [String]
    $routesToRemove: [String]
    $changedBy: String
    $note: String
  ) {
    upsertDepartmentAccess(
      department: $department
      routesToAdd: $routesToAdd
      routesToRemove: $routesToRemove
      changedBy: $changedBy
      note: $note
    ) {
      _id
      department
      routes
      updateHistory {
        changedBy
        changedAt
        changeType
        affectedRoutes
      }
      updatedAt
    }
  }
`;

const UPSERT_EMPLOYEE_ACCESS = gql`
  mutation UpsertEmployeeAccess(
    $employeeId: String!
    $employeeName: String
    $employeeDepartment: String
    $routesToAdd: [String]
    $routesToRemove: [String]
    $employeesToAdd: [String]
    $employeesToRemove: [String]
    $changedBy: String
    $note: String
  ) {
    upsertEmployeeAccess(
      employeeId: $employeeId
      employeeName: $employeeName
      employeeDepartment: $employeeDepartment
      routesToAdd: $routesToAdd
      routesToRemove: $routesToRemove
      employeesToAdd: $employeesToAdd
      employeesToRemove: $employeesToRemove
      changedBy: $changedBy
      note: $note
    ) {
      _id
      employeeId
      employeeName
      employeeDepartment
      routes
      employees
      updateHistory {
        changedBy
        changedAt
        changeType
        affectedRoutes
      }
      updatedAt
    }
  }
`;

// Helper function to check if data has actually changed
const hasDataChanged = (oldData, newData) => {
  if (!oldData && !newData) return false;
  if (!oldData || !newData) return true;
  if (oldData.length !== newData.length) return true;
  
  try {
    return JSON.stringify(oldData) !== JSON.stringify(newData);
  } catch (error) {
    console.error('Error comparing data:', error);
    return true;
  }
};

export const fetchAllDepartmentAccess = createAsyncThunk(
  "employeeAccess/fetchAllDepartmentAccess",
  async ({ includeInactive = false }, { rejectWithValue }) => {
    try {
      const { data } = await client.query({
        query: GET_ALL_DEPARTMENT_ACCESS,
        variables: { includeInactive },
        fetchPolicy: 'network-only', // Changed from 'no-cache' to 'network-only'
      });

      const allDepartmentAccess = data?.getAllDepartmentAccess;

      if (!allDepartmentAccess) {
        return rejectWithValue('No department access found');
      }

      return allDepartmentAccess;
    } catch (error) {
      console.error('Error in fetchAllDepartmentAccess:', error);
      return rejectWithValue(
        error?.message || error?.graphQLErrors?.[0]?.message || 'Failed to fetch all department access'
      );
    }
  }
);

// Upsert department access
export const upsertDepartmentAccess = createAsyncThunk(
  "employeeAccess/upsertDepartmentAccess",
  async ({ department, routesToAdd, routesToRemove, changedBy, note }, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: UPSERT_DEPARTMENT_ACCESS,
        variables: { department, routesToAdd, routesToRemove, changedBy, note },
        fetchPolicy: 'no-cache',
      });
      const updatedDepartment = data?.upsertDepartmentAccess;

      if (!updatedDepartment) {
        return rejectWithValue('Failed to update department access');
      }

      return updatedDepartment;
    } catch (error) {
      return rejectWithValue(error?.message || error?.graphQLErrors?.[0]?.message || error?.networkError?.message || 'Failed to save department access');
    }
  }
);


export const fetchAllEmployeeAccess = createAsyncThunk(
  "employeeAccess/fetchAllEmployeeAccess",
  async ({ includeInactive = false }, { rejectWithValue }) => {
    try {
      const { data } = await client.query({
        query: GET_ALL_EMPLOYEE_ACCESS,
        variables: { includeInactive },
        fetchPolicy: 'network-only', // Changed from 'no-cache'
      });

      const allEmployeeAccess = data?.getAllEmployeeAccess;

      if (!allEmployeeAccess) {
        return rejectWithValue('No employee access found');
      }

      return allEmployeeAccess;
    } catch (error) {
      return rejectWithValue(error?.message || error?.graphQLErrors?.[0]?.message || 'Failed to fetch all employee access');
    }
  }
);

export const upsertEmployeeAccess = createAsyncThunk(
  "employeeAccess/upsertEmployeeAccess",
  async ({ 
    employeeId, 
    employeeName, 
    employeeDepartment, 
    routesToAdd, 
    routesToRemove, 
    employeesToAdd, 
    employeesToRemove, 
    changedBy,
    note
  }, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: UPSERT_EMPLOYEE_ACCESS,
        variables: { employeeId, employeeName, employeeDepartment, routesToAdd, routesToRemove, employeesToAdd, employeesToRemove, changedBy, note },
        fetchPolicy: 'no-cache',
      });
      const updatedEmployee = data?.upsertEmployeeAccess;

      if (!updatedEmployee) {
        return rejectWithValue('Failed to update employee access');
      }

      return updatedEmployee;
    } catch (error) {
      return rejectWithValue(error?.message || error?.graphQLErrors?.[0]?.message || error?.networkError?.message || 'Failed to save employee access');
    }
  }
);

export const fetchEmployeeAllRoutes = createAsyncThunk(
  "employeeAccess/fetchEmployeeAllRoutes",
  async ({ employeeId, employeeDepartment }, { rejectWithValue }) => {
    try {
      const { data } = await client.query({
        query: GET_EMPLOYEE_ALL_ROUTES,
        variables: {
          employeeId,
          employeeDepartment,
        },
        fetchPolicy: 'network-only',
      });

      const routes = data?.getEmployeeAllRoutes;

      if (!routes) {
        return rejectWithValue('No routes found for employee');
      }

      return { employeeId, routes };
    } catch (error) {
      return rejectWithValue(error?.message || error?.graphQLErrors?.[0]?.message || 'Failed to fetch employee routes');
    }
  }
);

export const checkEmployeeRouteAccess = createAsyncThunk(
  "employeeAccess/checkEmployeeRouteAccess",
  async ({ employeeId, employeeDepartment, route }, { rejectWithValue }) => {
    try {
      const { data } = await client.query({
        query: CHECK_EMPLOYEE_ROUTE_ACCESS,
        variables: {
          employeeId,
          employeeDepartment,
          route,
        },
        fetchPolicy: 'network-only',
      });

      const hasAccess = data?.checkEmployeeRouteAccess;

      if (hasAccess === undefined || hasAccess === null) {
        return rejectWithValue('Unable to check route access');
      }

      return { route, hasAccess };
    } catch (error) {
      return rejectWithValue(error?.message || error?.graphQLErrors?.[0]?.message || 'Failed to check route access');
    }
  }
);

// Fetch Active employees who have Route Access to a specific route
// Used for Assign To dropdowns in Requests, Leads, Pipeline
export const fetchEmployeesByRoute = createAsyncThunk(
  "employeeAccess/fetchEmployeesByRoute",
  async ({ route }, { rejectWithValue }) => {
    try {
      const { data } = await client.query({
        query: GET_EMPLOYEES_BY_ROUTE,
        variables: { route },
        fetchPolicy: 'network-only',
      });
      const employees = data?.getEmployeesByRoute;
      if (!employees) {
        return rejectWithValue('No employees found for this route');
      }
      return { route, employees };
    } catch (error) {
      return rejectWithValue(
        error?.message || error?.graphQLErrors?.[0]?.message || 'Failed to fetch employees by route'
      );
    }
  }
);

const initialState = {
  currentDepartmentAccess: null,
  allDepartmentAccess: [],
  currentEmployeeAccess: null,
  allEmployeeAccess: [],
  employeeAllRoutes: {},
  routeAccessCache: {},
  routeFilteredEmployees: {}, // { "/request": [...], "/sales": [...], "/Pipeline": [...] }
  selectedEmployee: null,
  selectedDepartment: null,
  viewMode: "by_employee",
  searchTerm: "",
  loading: false,
  loadingDepartment: false,
  loadingEmployee: false,
  loadingAll: false,
  saving: false,
  error: null,
  departmentError: null,
  employeeError: null,
  lastSaved: null,
  dataLoaded: false, // Track if initial data has been loaded
};

const employeeAccessSlice = createSlice({
  name: "employeeAccess",
  initialState,
  reducers: {
    setSelectedEmployee: (state, action) => {
      state.selectedEmployee = action.payload;
    },
    setSelectedDepartment: (state, action) => {
      state.selectedDepartment = action.payload;
    },
    setViewMode: (state, action) => {
      state.viewMode = action.payload;
    },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    clearCurrentEmployeeAccess: (state) => {
      state.currentEmployeeAccess = null;
      state.employeeError = null;
    },
    clearCurrentDepartmentAccess: (state) => {
      state.currentDepartmentAccess = null;
      state.departmentError = null;
    },
    clearErrors: (state) => {
      state.error = null;
      state.departmentError = null;
      state.employeeError = null;
    },
    resetEmployeeAccessState: () => initialState,
    updateLocalDepartmentRoutes: (state, action) => {
      const { department, routes } = action.payload;
      
      if (state.currentDepartmentAccess?.department === department) {
        state.currentDepartmentAccess.routes = routes;
      }
      
      const index = state.allDepartmentAccess.findIndex(dept => dept.department === department);
      if (index !== -1) {
        state.allDepartmentAccess[index] = {
          ...state.allDepartmentAccess[index],
          routes: routes
        };
      }
    },
    updateLocalEmployeeRoutes: (state, action) => {
      const { employeeId, routes } = action.payload;
      if (state.currentEmployeeAccess?.employeeId === employeeId) {
        state.currentEmployeeAccess.routes = routes;
      }
      const index = state.allEmployeeAccess.findIndex(emp => emp.employeeId === employeeId);
      if (index !== -1) {
        state.allEmployeeAccess[index] = {
          ...state.allEmployeeAccess[index],
          routes: routes
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllDepartmentAccess.pending, (state) => {
        state.loadingAll = true;
        state.error = null;
      })
      .addCase(fetchAllDepartmentAccess.fulfilled, (state, action) => {
        state.loadingAll = false;
        state.error = null;
        state.dataLoaded = true;
        
        // Only update if data has actually changed
        const newData = action.payload || [];
        if (hasDataChanged(state.allDepartmentAccess, newData)) {
          state.allDepartmentAccess = newData;
        }
      })
      .addCase(fetchAllDepartmentAccess.rejected, (state, action) => {
        state.loadingAll = false;
        state.error = action.payload;
        state.dataLoaded = true;
      });
    
    builder
      .addCase(upsertDepartmentAccess.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(upsertDepartmentAccess.fulfilled, (state, action) => {
        state.saving = false;
        state.currentDepartmentAccess = action.payload;
        state.lastSaved = new Date().toISOString();
        state.error = null;
        
        // Update in allDepartmentAccess array immutably
        const index = state.allDepartmentAccess.findIndex(
          dept => dept.department === action.payload.department
        );
        
        if (index !== -1) {
          // Replace the item at the index with new data
          state.allDepartmentAccess = [
            ...state.allDepartmentAccess.slice(0, index),
            action.payload,
            ...state.allDepartmentAccess.slice(index + 1)
          ];
        } else {
          // Add new item
          state.allDepartmentAccess = [...state.allDepartmentAccess, action.payload];
        }
      })
      .addCase(upsertDepartmentAccess.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });
    
    // builder
      // .addCase(fetchEmployeeAccess.pending, (state) => {
      //   state.loadingEmployee = true;
      //   state.employeeError = null;
      // })
      // .addCase(fetchEmployeeAccess.fulfilled, (state, action) => {
      //   state.loadingEmployee = false;
      //   state.currentEmployeeAccess = action.payload;
      //   state.employeeError = null;
      // })
      // .addCase(fetchEmployeeAccess.rejected, (state, action) => {
      //   state.loadingEmployee = false;
      //   state.employeeError = action.payload;
      // });
    
    builder
      .addCase(fetchAllEmployeeAccess.pending, (state) => {
        state.loadingAll = true;
        state.error = null;
      })
      .addCase(fetchAllEmployeeAccess.fulfilled, (state, action) => {
        state.loadingAll = false;
        state.error = null;
        state.dataLoaded = true;
        
        // Only update if data has actually changed
        const newData = action.payload || [];
        if (hasDataChanged(state.allEmployeeAccess, newData)) {
          state.allEmployeeAccess = newData;
        }
      })
      .addCase(fetchAllEmployeeAccess.rejected, (state, action) => {
        state.loadingAll = false;
        state.error = action.payload;
        state.dataLoaded = true;
      });
    
    builder
      .addCase(upsertEmployeeAccess.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(upsertEmployeeAccess.fulfilled, (state, action) => {
        state.saving = false;
        state.currentEmployeeAccess = action.payload;
        state.lastSaved = new Date().toISOString();
        state.error = null;
        
        // Update in allEmployeeAccess array immutably
        const index = state.allEmployeeAccess.findIndex(
          emp => emp.employeeId === action.payload.employeeId
        );
        
        if (index !== -1) {
          // Replace the item at the index with new data
          state.allEmployeeAccess = [
            ...state.allEmployeeAccess.slice(0, index),
            action.payload,
            ...state.allEmployeeAccess.slice(index + 1)
          ];
        } else {
          // Add new item
          state.allEmployeeAccess = [...state.allEmployeeAccess, action.payload];
        }
      })
      .addCase(upsertEmployeeAccess.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });
    
    builder
      .addCase(fetchEmployeeAllRoutes.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEmployeeAllRoutes.fulfilled, (state, action) => {
        state.loading = false;
        const { employeeId, routes } = action.payload;
        // Only update if changed
        if (JSON.stringify(state.employeeAllRoutes[employeeId]) !== JSON.stringify(routes)) {
          state.employeeAllRoutes = {
            ...state.employeeAllRoutes,
            [employeeId]: routes
          };
        }
      })
      .addCase(fetchEmployeeAllRoutes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    
    builder
      .addCase(checkEmployeeRouteAccess.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkEmployeeRouteAccess.fulfilled, (state, action) => {
        state.loading = false;
        const { route, hasAccess } = action.payload;
        // Only update if changed
        if (state.routeAccessCache[route] !== hasAccess) {
          state.routeAccessCache = {
            ...state.routeAccessCache,
            [route]: hasAccess
          };
        }
      })
      .addCase(checkEmployeeRouteAccess.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchEmployeesByRoute.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEmployeesByRoute.fulfilled, (state, action) => {
        state.loading = false;
        const { route, employees } = action.payload;
        state.routeFilteredEmployees = {
          ...state.routeFilteredEmployees,
          [route]: employees,
        };
      })
      .addCase(fetchEmployeesByRoute.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSelectedEmployee,
  setSelectedDepartment,
  setViewMode,
  setSearchTerm,
  clearCurrentEmployeeAccess,
  clearCurrentDepartmentAccess,
  clearErrors,
  resetEmployeeAccessState,
  updateLocalDepartmentRoutes,
  updateLocalEmployeeRoutes,
} = employeeAccessSlice.actions;

export default employeeAccessSlice.reducer;